import { TableActionButton, TableActionContainer } from '@core/components/TableAction';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Button, Card, CardActions, CardContent, Grid, makeStyles } from '@material-ui/core';
import { ClearAll, CompareArrows, Edit } from '@material-ui/icons';
import { getCurrencySemSinalMonetario } from 'app/utils/monetarioUtil';
import React from 'react';
import { FaEye, FaPlus, FaSearch } from 'react-icons/fa';

const useStyles = makeStyles((theme) => ({
  btnContainer: {
    alignSelf: 'center',
  },
  btn: {
    background: '#5890aa',
    color: '#fff',
    width: '100%',
    height: 36,
    borderRadius: 5,
    textTransform: 'initial',
    transition: '0.4s',
    '&:hover': {
      background: '#335674',
    },
  },
  containerExtrato: {
    minHeight: '269px',
    maxHeight: '608px',
    overflow: 'scroll',
  },
  customIcons: {
    fontSize: '16px',
  },
  buttonEditDisabled: {
    cursor: 'default',
    color: 'white',
    borderRadius: '30%',
    padding: 4,
    border: 0,
    backgroundColor: 'rgb(157, 157, 157, 0.8)',
    '&:hover': {
      backgroundColor: 'rgb(157, 157, 157, 1)',
      boxShadow: 'none',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: 'rgb(157, 157, 157, 0.9)',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
    '&:focus': {
      boxShadow: '0 0 0 0.2rem rgb(157, 157, 157, 0.9)',
      borderColor: 'rgb(157, 157, 157, 0)',
    },
  },
}));

const ContainerExtratoForm = ({
  values,
  children,
  handlerPesquisar,
  handlerVisualizar,
  handlerEditar,
  handlerLancarConta,
  handlerCriarTransferencia,
  handlerLimparInformacoes,
  handlerDesfazerTransferencia,
  formReceitaDespesa,
  formTransferencia,
  temPermissaoParaVisualizar,
  temPermissaoParaEditar,
  temPermissaoParaCriar,
  formik,
}) => {
  const classes = useStyles();

  const isReceitaDespesa =
    values?.contaReceberList &&
    values?.contaReceberList.length > 0 &&
    values?.contaPagarList &&
    values?.contaPagarList.length > 0;

  const disableLancarPesquisarButton =
    (values?.contaReceberList?.length === 1 && values?.contaReceberList[0]?.situacao === 'PARCIAL') ||
    (values?.contaPagarList?.length === 1 && values?.contaPagarList[0]?.situacao === 'PARCIAL');

  const disableEditButton =
    values?.contaReceberList?.length === 1 &&
    values?.contaReceberList[0]?.receitaCobranca?.situacaoRemessa === 'GERADA' &&
    values?.contaReceberList[0]?.receitaCobranca?.tipoCobranca !== 'PARTNER_BANK';

  const disableEditButtonReenviada =
    values?.contaReceberList?.length === 1 &&
    values?.contaReceberList[0]?.receitaCobranca?.situacaoRemessa === 'REENVIADA' &&
    values?.contaReceberList[0]?.receitaCobranca?.tipoCobranca === 'PARTNER_BANK';

  const total = (contaPagarList, contaReceberList, transferenciaBancaria) => {
    let total = 0;
    if (transferenciaBancaria) {
      total = transferenciaBancaria?.valor;
    } else if (isReceitaDespesa) {
      let totalDespesa = 0;
      let totalReceita = 0;
      if (contaPagarList && contaPagarList.length > 0) {
        contaPagarList.forEach((contaPagar) => {
          totalDespesa += contaPagar?.formulario
            ? formik.values.valorDespesa
            : contaPagar?.baixa
            ? contaPagar?.baixa?.valorPago
            : contaPagar.valor
            ? contaPagar.valor
            : 0;
        });
      }
      if (contaReceberList && contaReceberList.length > 0) {
        contaReceberList.forEach((contaReceber) => {
          totalReceita += contaReceber?.formulario
            ? formik.values.valorReceita
            : contaReceber?.baixa
            ? contaReceber?.baixa?.valorPago
            : contaReceber.valor
            ? contaReceber.valor
            : 0;
        });
      }
      total = totalReceita - totalDespesa;
    } else if (contaPagarList) {
      contaPagarList.forEach((contaPagar) => {
        total += contaPagar?.baixa ? contaPagar?.baixa?.valorPago : contaPagar.valor ? contaPagar.valor : 0;
      });
    } else if (contaReceberList) {
      contaReceberList.forEach((contaReceber) => {
        total += contaReceber?.baixa ? contaReceber?.baixa?.valorPago : contaReceber.valor ? contaReceber.valor : 0;
      });
    }
    return total;
  };

  const diferenca = (valorExtrato, contaPagarList, contaReceberList) => {
    let diferenca = 0;
    const totalLancamentos = total(contaPagarList, contaReceberList, undefined);

    if (valorExtrato > 0) {
      diferenca = valorExtrato - totalLancamentos;
    } else {
      diferenca = valorExtrato + totalLancamentos;
    }

    return diferenca;
  };

  const exibirDiferencas = () => {
    return (
      ((!!values?.contaPagarList && values?.contaPagarList.length > 1) ||
        (!!values?.contaReceberList && values?.contaReceberList.length > 1) ||
        (values.contaReceberList &&
          values.contaReceberList.length === 1 &&
          values.contaPagarList &&
          values.contaPagarList.length === 1)) &&
      diferenca(values?.valor, values?.contaPagarList, values?.contaReceberList) !== 0
    );
  };

  const exibirIconeVisualizar = () => {
    return (
      !values.transferenciaBancaria &&
      ((!!values.conciliado &&
        !!values?.contaPagarList &&
        values?.contaPagarList?.filter((lancamento) => !lancamento.formulario).length === 1 &&
        !values?.contaReceberList) ||
        (!!values?.contaPagarList &&
          values?.contaPagarList?.filter((lancamento) => !lancamento.formulario).length === 1) ||
        (values.conciliado &&
          !!values?.contaReceberList &&
          values?.contaReceberList?.filter((lancamento) => !lancamento.formulario).length === 1 &&
          !values?.contaPagarList) ||
        (!!values?.contaReceberList &&
          values?.contaReceberList?.filter((lancamento) => !lancamento.formulario).length === 1))
    );
  };

  const exibirIconeEditar = () => {
    return (
      values.valor !== undefined &&
      values.valor !== 0 &&
      !values.conciliado &&
      !values.criarTransferencia &&
      !values.transferenciaBancaria &&
      ((!!values?.contaPagarList &&
        values?.contaPagarList?.filter((lancamento) => !lancamento.formulario).length === 1) ||
        (!!values?.contaReceberList &&
          values?.contaReceberList?.filter((lancamento) => !lancamento.formulario).length === 1))
    );
  };

  const exibirIconePesquisar = () => {
    return values.valor !== undefined && values.valor !== 0 && !values.conciliado && !values.transferenciaBancaria;
  };

  const exibirIconeLancar = () => {
    return (
      values.valor !== undefined &&
      values.valor !== 0 &&
      !values.conciliado &&
      !values.criarTransferencia &&
      !values.transferenciaBancaria
    );
  };

  const exibirIconeCriartransferencia = () => {
    return (
      values.valor !== undefined &&
      values.valor !== 0 &&
      !values.conciliado &&
      !values.criarTransferencia &&
      !values.transferenciaBancaria
    );
  };

  const exibirIconeLimpar = () => {
    return (
      values.valor !== undefined &&
      values.valor !== 0 &&
      !values?.conciliado &&
      (!values?.contaPagarList ||
        (values?.contaPagarList &&
          values?.contaPagarList?.filter((lancamento) => !lancamento.formulario).length === 1) ||
        !values?.contaReceberList ||
        (values?.contaReceberList &&
          values?.contaReceberList?.filter((lancamento) => !lancamento.formulario).length === 1))
    );
  };

  const handleEditTitle = () => {
    if (disableEditButton) {
      return 'Este boleto já foi incluído em uma remessa. Aguarde o retorno do banco.';
    } else if (disableEditButtonReenviada) {
      return 'Não é possível alterar cobrança sem nosso número gerado. Aguarde a Partner Bank gerar o nosso número para realizar a alteração.';
    } else {
      return 'Editar';
    }
  };

  return (
    <Card variant="outlined">
      <CardContent className={classes.containerExtrato}>{children}</CardContent>
      <CardActions>
        <Grid container>
          <Grid item xs={exibirDiferencas() ? 4 : 3}>
            {exibirDiferencas() ? (
              <>
                <b>Total (R$):</b>
                <span style={{ color: 'red' }}>{` ${getCurrencySemSinalMonetario(
                  total(values?.contaPagarList, values?.contaReceberList, values?.transferenciaBancaria).toFixed(2)
                )}`}</span>
              </>
            ) : (
              <></>
            )}
          </Grid>
          <Grid item xs={exibirDiferencas() ? 4 : 3}>
            {exibirDiferencas() ? (
              <>
                <b>Diferença (R$):</b>
                <span style={{ color: 'red' }}>{` ${getCurrencySemSinalMonetario(
                  Math.abs(diferenca(values?.valor, values?.contaPagarList, values?.contaReceberList)).toFixed(2)
                )}`}</span>
              </>
            ) : (
              <></>
            )}
          </Grid>
          <Grid item xs={exibirDiferencas() ? 4 : 6} style={{ textAlign: 'end' }}>
            <TableActionContainer>
              {exibirIconeVisualizar() && temPermissaoParaVisualizar && !formReceitaDespesa && !isReceitaDespesa && (
                <TableActionButton
                  title="Visualizar"
                  icon={<FaEye size={16} />}
                  onClick={() =>
                    handlerVisualizar(
                      values?.contaPagarList
                        ? values?.contaPagarList[0]
                        : values?.contaReceberList
                        ? values?.contaReceberList[0]
                        : values?.transferenciaBancaria
                        ? values?.transferenciaBancaria
                        : undefined
                    )
                  }
                />
              )}
              {exibirIconeEditar() && temPermissaoParaEditar && !formReceitaDespesa && (
                <TableActionButton
                  title={handleEditTitle()}
                  style={(disableEditButton || disableEditButtonReenviada) && classes.buttonEditDisabled}
                  icon={<Edit size={12} className={classes.customIcons} />}
                  onClick={() => {
                    if (!disableEditButton && !disableEditButtonReenviada) {
                      handlerEditar(
                        values?.contaPagarList
                          ? values?.contaPagarList[0]
                          : values?.contaReceberList
                          ? values?.contaReceberList[0]
                          : values?.transferenciaBancaria
                          ? values?.transferenciaBancaria
                          : undefined
                      );
                    }
                  }}
                />
              )}
              {exibirIconePesquisar() && temPermissaoParaVisualizar && !formReceitaDespesa && (
                <TableActionButton
                  title={formTransferencia ? 'Buscar transferência' : 'Pesquisar conta'}
                  style={disableLancarPesquisarButton && classes.buttonEditDisabled}
                  icon={<FaSearch size={16} />}
                  onClick={() => {
                    if (!disableLancarPesquisarButton) {
                      handlerPesquisar();
                    }
                  }}
                />
              )}
              {exibirIconeLancar() && temPermissaoParaCriar && !formReceitaDespesa && (
                <TableActionButton
                  title="Lançar conta"
                  style={disableLancarPesquisarButton && classes.buttonEditDisabled}
                  icon={<FaPlus size={16} />}
                  onClick={() => {
                    if (!disableLancarPesquisarButton) {
                      handlerLancarConta();
                    }
                  }}
                />
              )}
              {exibirIconeCriartransferencia() && !formReceitaDespesa && permissaoUtils.transferencia().cadastrar && (
                <TableActionButton
                  title="Criar transferência"
                  icon={<CompareArrows size={12} className={classes.customIcons} />}
                  onClick={() => handlerCriarTransferencia()}
                />
              )}
              {exibirIconeLimpar() && !formReceitaDespesa && (
                <TableActionButton
                  title="Limpar informações"
                  icon={<ClearAll size={12} className={classes.customIcons} />}
                  onClick={() => handlerLimparInformacoes()}
                />
              )}
            </TableActionContainer>
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
};

const ExtratoItem = ({ info, form, button }) => {
  return (
    <Grid container style={{ padding: '1em' }}>
      <Grid item xs={5}>
        {info}
      </Grid>
      {form}
    </Grid>
  );
};

const BotaoConciliar = ({ onClick }) => {
  const classes = useStyles();
  return (
    <Grid container>
      <Grid item xs={2} />
      <Grid item xs={8}>
        <Button variant="contained" className={classes.btn} onClick={onClick}>
          Conciliar
        </Button>
      </Grid>
      <Grid item xs={2} />
    </Grid>
  );
};

const BotaoDesconciliar = ({ onClick }) => {
  const classes = useStyles();
  return (
    <Grid container>
      <Grid item xs={2} />
      <Grid item xs={8}>
        <Button variant="contained" className={classes.btn} onClick={onClick}>
          Desconciliar
        </Button>
      </Grid>
      <Grid item xs={2} />
    </Grid>
  );
};

const ContainerLancamentoExtratoBancario = ({ children }) => {
  return <>{children}</>;
};

export { BotaoConciliar, BotaoDesconciliar, ContainerExtratoForm, ContainerLancamentoExtratoBancario, ExtratoItem };
